import { withoutNullable } from "@syla/shared/types/helpers/withoutNullable";
import { ImportType } from "@syla/shared/types/models/ImportType";
import { TransactionTagType } from "@syla/shared/types/models/TransactionBase";
import { ImportFilter } from "@syla/shared/types/requests/GetGroupsRequest";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import { format } from "date-fns";
import { groupBy, sumBy, orderBy } from "lodash";
import React, { useMemo, useState } from "react";
import noPaddingMissingImageIcon from "../../../images/thumbnails/noPaddingMissingImageIcon.svg";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { useQueryDataSources } from "../../../store/useQueryDataSources";
import { useQueryGetAccountImports } from "../../../store/useQueryGetAccountImports";
import { StandardSelectOption } from "../../selector/parts/SelectOption";
import { Selector, SelectorProps } from "../../selector/Selector";

export type ImportItem = {
  _id: string;
  type: ImportType;
  date: Date;
  fileName?: string;
  dataSource: DataSourceWithNetworks | undefined;
  transactionCount: number;
};

type ImportFilterSelectorProps = {
  values: ImportFilter[];
  onChange: (values: ImportFilter[]) => void;
  matchWidth?: boolean;
} & Omit<
  SelectorProps<TransactionTagType>,
  "onChange" | "options" | "values" | "isLoading" | "placeholder"
>;

export function ImportFilterSelector({
  values,
  onChange,
  ...props
}: ImportFilterSelectorProps): React.ReactElement {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const [shouldLoadOptions, setShouldLoadOptions] = useState(false);

  const queryGetAccountImports = useQueryGetAccountImports(
    accountId,
    shouldLoadOptions,
  );

  const queryDataSources = useQueryDataSources(shouldLoadOptions);

  const importsData = queryGetAccountImports.data;

  const dataSourcesData = queryDataSources.data;

  const importItems = useMemo(() => {
    if (!dataSourcesData || !importsData) {
      return undefined;
    }

    return importsData.imports.map((details) => {
      // manual imports are combined across data sources
      const dataSource =
        details.import.type === ImportType.manual ?
          undefined
        : dataSourcesData?.find((ds) => ds._id === details.import.dataSource);

      const option: ImportItem = {
        _id: details.import._id,
        type: details.import.type,
        date: details.import.date,
        fileName: details.import.fileName,
        transactionCount: details.transactionCount,
        dataSource,
      };

      return option;
    });
  }, [dataSourcesData, importsData]);

  const importOptions = useMemo(() => {
    if (!importItems) {
      return undefined;
    }

    const sorted = orderBy(importItems, (o) => o.date, "desc");

    // group imports by their import id, except for manual imports which are all collected together.
    const grouped = groupBy(sorted, (item) =>
      item.type === ImportType.manual ? item.type : item._id,
    );

    return Object.entries(grouped).map(([key, item]) => {
      const label =
        item[0].type === ImportType.file ? `${item[0].fileName}`
        : item[0].type === ImportType.sync ?
          `${item[0].dataSource?.name || "Unknown"} Sync`
        : "Manually Added";

      const tag = sumBy(
        item,
        (importItem) => importItem.transactionCount,
      ).toLocaleString();

      // file imports don't include the data source name in the label, so we
      // add it as an additional search term.
      const keywords = withoutNullable([label, item[0].dataSource?.name]);

      const option: StandardSelectOption = {
        value: key,
        keywords,
        tooltip: label,
        data: {
          label,
          icon: item[0].dataSource?.image ?? noPaddingMissingImageIcon,
          description: format(item[0].date, "d MMMM yyyy h:mm a"),
          tag,
        },
      };

      return option;
    });
  }, [importItems]);

  const isLoading =
    shouldLoadOptions &&
    (queryGetAccountImports.isLoading || queryDataSources.isLoading);

  return (
    <Selector
      isLoading={isLoading}
      onOpen={() => {
        setShouldLoadOptions(true);
      }}
      enableSearch
      enableBulkSelect
      enableDeselect
      enableMultiSelect
      moveSelectedToTop
      values={values.map((item) => {
        return item._id ?? item.type;
      })}
      onChange={(values) => {
        const items = (importItems || []).filter((item) => {
          const key = item.type === ImportType.manual ? item.type : item._id;
          return values.some((value) => value === key);
        });

        onChange(
          items.map((option) =>
            option.type === ImportType.manual ?
              { type: option.type }
            : { _id: option._id },
          ),
        );
      }}
      options={importOptions || []}
      placeholder="Imports"
      {...props}
    />
  );
}
